import { useState, useMemo, useRef, useCallback } from "react";
import { format } from "date-fns";
import {
  useCreateRecord, useAiPolishText,
  usePersonalNccdAdjustments, useGetClassStudents,
  useGetProfile,
  customFetch,
} from "@workspace/api-client-react";
import { useNccdPolish } from "@workspace/api-client-react";
import type { RedactionSummary } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorBody, getApiErrorMessage } from "@/lib/api-error";
import { DataMinimisationTip } from "@/components/DataMinimisationTip";
import { compressImage } from "@/lib/image-compress";
import { AiRedactionDisclosure } from "@/components/ai/AiRedactionDisclosure";
import { AIFeedback } from "@/components/ai/AIFeedback";
import {
  formatStudentName,
  studentSortKey,
  useDefaultStudentDisplayMode,
  type Student as DisplayStudent,
} from "@/lib/studentDisplay";
import { Button } from "@/components/ui/button";
import { AutoResizeTextarea } from "@/components/ui/auto-resize-textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sparkles, Loader2, Users, Check, ChevronDown, ChevronUp, Search, Star, X, ChevronsUpDown,
  Camera, ImagePlus, Trash2, Mic, Square,
} from "lucide-react";
import { useSpeechInput } from "@/hooks/use-speech-input";
import {
  NCCD_ADJUSTMENTS, groupByCategory, type NccdAdjustment,
} from "@/lib/nccd-adjustments";

// ─── Types ────────────────────────────────────────────────────────────────────

export type RecordType = "general" | "nccd" | "contact" | "behaviour";

const RECORD_TYPE_META: Record<RecordType, { label: string; pill: string; active: string }> = {
  general:   { label: "General Note",    pill: "bg-gray-100 text-gray-700",     active: "bg-gray-600 text-white border-gray-600"   },
  nccd:      { label: "NCCD Adjustment", pill: "bg-blue-100 text-blue-700",     active: "bg-blue-600 text-white border-blue-600"   },
  contact:   { label: "Contact Note",    pill: "bg-green-100 text-green-700",   active: "bg-green-600 text-white border-green-600" },
  behaviour: { label: "Behaviour Note",  pill: "bg-purple-100 text-purple-700", active: "bg-purple-600 text-white border-purple-600" },
};

// ─── NCCD Adjustment Panel ────────────────────────────────────────────────────

interface AdjustmentPanelProps {
  adjustments: NccdAdjustment[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  personalIds: string[];
  aiDetectedIds: string[];
}

function AdjustmentPanel({ adjustments, selectedIds, onToggle, personalIds, aiDetectedIds }: AdjustmentPanelProps) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());

  const adjustmentMap = useMemo(() => new Map(adjustments.map((a) => [a.id, a])), [adjustments]);
  const adjustmentsByCategory = useMemo(() => groupByCategory(adjustments), [adjustments]);

  const toggleCategory = (cat: string) =>
    setOpenCategories((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });

  const filteredAdjustments = useMemo(() => {
    if (!search) return null;
    const q = search.toLowerCase();
    return adjustments.filter(
      (a) => a.label.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q)
    );
  }, [search, adjustments]);

  const personalTop = useMemo(
    () => personalIds.slice(0, 6).map((id: string) => adjustmentMap.get(id)).filter(Boolean) as NccdAdjustment[],
    [personalIds, adjustmentMap]
  );

  const selectedCount = selectedIds.length;

  return (
    <div className="border border-blue-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 border-b border-blue-100">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-blue-900">Adjustments Made</span>
          {selectedCount > 0 && (
            <Badge className="bg-blue-600 text-white border-0 text-xs py-0 h-5">{selectedCount}</Badge>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-blue-700 hover:underline flex items-center gap-1"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? "Collapse" : "Browse all"}
        </button>
      </div>

      <div className="p-3 space-y-3 bg-white">
        {selectedCount > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedIds.map((id) => {
              const adj = adjustmentMap.get(id);
              if (!adj) return null;
              const isNew = aiDetectedIds.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => onToggle(id)}
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                    isNew
                      ? "bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
                      : "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  }`}
                >
                  {isNew && <Sparkles className="h-3 w-3 text-purple-500" />}
                  {adj.label}
                  <X className="h-2.5 w-2.5 text-current opacity-50" />
                </button>
              );
            })}
          </div>
        )}

        {aiDetectedIds.length > 0 && (
          <div className="flex items-start gap-2 text-xs text-purple-700 bg-purple-50 rounded-lg px-3 py-2 border border-purple-200">
            <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-purple-500" />
            <span>
              AI detected {aiDetectedIds.length} adjustment{aiDetectedIds.length !== 1 ? "s" : ""} from your notes.
              Pre-selected above — uncheck any that don't apply.
            </span>
          </div>
        )}

        {personalTop.length > 0 && !expanded && (
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              <Star className="h-3 w-3" /> Your most used
            </div>
            <div className="flex flex-wrap gap-1.5">
              {personalTop.map((adj) => {
                const sel = selectedIds.includes(adj!.id);
                return (
                  <button
                    key={adj!.id}
                    type="button"
                    onClick={() => onToggle(adj!.id)}
                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      sel
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-700"
                    }`}
                  >
                    {sel && <Check className="h-3 w-3" />}
                    {adj!.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {expanded && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search adjustments…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {filteredAdjustments ? (
              <div className="flex flex-wrap gap-1.5">
                {filteredAdjustments.map((adj) => {
                  const sel = selectedIds.includes(adj.id);
                  return (
                    <button
                      key={adj.id}
                      type="button"
                      onClick={() => onToggle(adj.id)}
                      title={adj.description}
                      className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        sel
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-700"
                      }`}
                    >
                      {sel && <Check className="h-3 w-3" />}
                      {adj.label}
                    </button>
                  );
                })}
                {filteredAdjustments.length === 0 && (
                  <p className="text-sm text-gray-400">No adjustments match "{search}"</p>
                )}
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {Object.entries(adjustmentsByCategory).map(([cat, adjs]) => {
                  const isOpen = openCategories.has(cat);
                  const catSelected = adjs.filter((a) => selectedIds.includes(a.id)).length;
                  return (
                    <div key={cat} className="border border-gray-100 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <span className="text-xs font-semibold text-gray-700">{cat}</span>
                        <div className="flex items-center gap-2">
                          {catSelected > 0 && (
                            <span className="text-xs bg-blue-600 text-white rounded-full px-1.5 py-0.5 font-medium">{catSelected}</span>
                          )}
                          {isOpen ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="p-2.5 flex flex-wrap gap-1.5">
                          {adjs.map((adj) => {
                            const sel = selectedIds.includes(adj.id);
                            return (
                              <button
                                key={adj.id}
                                type="button"
                                onClick={() => onToggle(adj.id)}
                                title={adj.description}
                                className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                  sel
                                    ? "bg-blue-600 border-blue-600 text-white"
                                    : "bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-700"
                                }`}
                              >
                                {sel && <Check className="h-3 w-3" />}
                                {adj.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {selectedCount === 0 && !expanded && personalTop.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-1">
            Polish with AI to auto-detect, or browse all to add manually.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export interface RecordFormPanelProps {
  date: Date;
  slot: {
    id: number;
    label: string;
    courseId?: number | null;
    courseName?: string | null;
    classId?: number | null;
  };
  classStudents: {
    id: number;
    firstName: string;
    lastName?: string | null;
    preferredName?: string | null;
    studentCode?: string | null;
    displayMode?: string | null;
    yearLevel?: string | null;
  }[];
  onSaved: () => void;
  onCancel: () => void;
}

interface PhotoItem {
  previewUrl: string;  // local blob URL for thumbnail
  objectPath: string;  // GCS path returned after upload
  uploading?: boolean;
}

export function RecordFormPanel({ date, slot, classStudents, onSaved, onCancel }: RecordFormPanelProps) {
  const [type, setType]               = useState<RecordType>("general");
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [shorthandNotes, setShorthandNotes] = useState("");
  const [description, setDescription] = useState("");
  const [selectedAdjustments, setSelectedAdjustments] = useState<string[]>([]);
  const [aiDetectedIds, setAiDetectedIds] = useState<string[]>([]);
  const [polishRedaction, setPolishRedaction] = useState<RedactionSummary | null>(null);
  const [polishAiUsageId, setPolishAiUsageId] = useState<number | null>(null);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const { data: profileData } = useGetProfile();
  const { data: personalBank } = usePersonalNccdAdjustments();
  const { data: classStudentsData } = useGetClassStudents(slot.classId ?? 0, {
    query: { enabled: !!slot.classId },
  });
  const createRecord = useCreateRecord();

  const effectiveAdjustments = useMemo(() => {
    const userPrefs = (profileData as any)?.preferences;
    const hiddenIds: string[] = userPrefs?.hiddenAdjustmentIds ?? [];
    const renamedMap: Record<string, string> = userPrefs?.renamedAdjustments ?? {};
    const customAdjs: NccdAdjustment[] = (userPrefs?.customAdjustments ?? []).map((c: any) => ({
      id: c.id,
      label: c.label,
      category: c.category,
    }));
    const standard = NCCD_ADJUSTMENTS
      .filter((a) => !hiddenIds.includes(a.id))
      .map((a) => ({ ...a, label: renamedMap[a.id] ?? a.label }));
    return [...standard, ...customAdjs];
  }, [profileData]);
  const polishText   = useAiPolishText();
  const nccdPolish   = useNccdPolish();
  const queryClient  = useQueryClient();
  const { toast }    = useToast();

  const personalIds = useMemo(
    () => (personalBank?.adjustments ?? []).map((a: { id: string }) => a.id),
    [personalBank]
  );

  const accountDisplayMode = useDefaultStudentDisplayMode();
  const filteredStudents = classStudents
    .filter((s) => {
      const haystack = [s.firstName, s.lastName, s.preferredName, s.studentCode]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(studentSearch.toLowerCase());
    })
    .slice()
    .sort((a, b) => studentSortKey(a as DisplayStudent).localeCompare(studentSortKey(b as DisplayStudent)));

  const toggleStudent = (id: number) =>
    setSelectedStudentIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const uploadPhoto = useCallback(async (rawFile: File) => {
    const previewUrl = URL.createObjectURL(rawFile);
    const placeholder: PhotoItem = { previewUrl, objectPath: "", uploading: true };
    setPhotos((prev) => [...prev, placeholder]);
    setPhotoUploading(true);
    try {
      // Task #138 — compress + EXIF-strip in the browser before requesting a
      // presigned URL. The 1.5 MB server-side cap (routes/storage.ts) will
      // still reject anything still oversize after compression, with a
      // clear error message.
      const file = await compressImage(rawFile);

      // Step 1: get presigned URL
      const { uploadURL, objectPath } = await customFetch<{ uploadURL: string; objectPath: string }>("/api/storage/uploads/request-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type || "image/jpeg" }),
      });

      // Step 2: upload directly to GCS
      const uploadRes = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "image/jpeg" },
      });
      if (!uploadRes.ok) throw new Error("Upload to storage failed");

      // Task #138 — confirm AFTER the PUT succeeds so per-teacher
      // image_storage_bytes counts only bytes actually in S3.
      try {
        await customFetch("/api/storage/uploads/confirm", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ size: file.size }),
        });
      } catch {
        // best-effort — file is already in S3
      }

      setPhotos((prev) =>
        prev.map((p) => p.previewUrl === previewUrl ? { previewUrl, objectPath, uploading: false } : p)
      );
    } catch (err) {
      setPhotos((prev) => prev.filter((p) => p.previewUrl !== previewUrl));
      URL.revokeObjectURL(previewUrl);
      // api-error-helper-exempt: presigned S3 PUT (not ApiError); helper still produces a sensible fallback.
      toast({
        title: "Photo upload failed",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    } finally {
      setPhotoUploading(false);
    }
  }, [toast]);

  const handlePhotoFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach((f) => { if (f.type.startsWith("image/")) uploadPhoto(f); });
  }, [uploadPhoto]);

  const removePhoto = (previewUrl: string) => {
    setPhotos((prev) => {
      const removed = prev.find((p) => p.previewUrl === previewUrl);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
        // Task #148 — refund image_storage_bytes for photos the teacher
        // attached then removed before saving. Best-effort: a failed
        // refund just leaves the operator counter slightly inflated.
        if (removed.objectPath && !removed.uploading) {
          customFetch("/api/storage/uploads/refund", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ objectPaths: [removed.objectPath] }),
          }).catch(() => { /* best-effort */ });
        }
      }
      return prev.filter((p) => p.previewUrl !== previewUrl);
    });
  };

  const toggleAdjustment = (id: string) =>
    setSelectedAdjustments((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const runPolish = async () => {
    if (!shorthandNotes.trim()) return;
    const student = selectedStudentIds.length === 1
      ? classStudents.find((s) => s.id === selectedStudentIds[0])
      : undefined;
    const studentId = student?.id;

    try {
      if (type === "nccd") {
        const effectiveIds = effectiveAdjustments.map((a) => a.id);
        const result = await nccdPolish.mutateAsync({
          text: shorthandNotes,
          studentId,
          adjustmentIds: effectiveIds,
        });
        setDescription(result.polished);
        setPolishRedaction(result.redaction ?? null);
        setPolishAiUsageId(result.aiUsageId ?? null);
        const newDetected = result.adjustments.filter((id) => effectiveIds.includes(id));
        setAiDetectedIds(newDetected);
        setSelectedAdjustments((prev) => [...new Set([...prev, ...newDetected])]);
        toast({
          title: "Notes polished!",
          description: newDetected.length > 0
            ? `${newDetected.length} adjustment${newDetected.length !== 1 ? "s" : ""} detected.`
            : "Rewritten as a professional NCCD record.",
        });
      } else {
        const result = await polishText.mutateAsync({
          data: { text: shorthandNotes, recordType: type as any, studentId },
        });
        setDescription(result.polished);
        setPolishRedaction(result.redaction ?? null);
        setPolishAiUsageId(result.aiUsageId ?? null);
        toast({ title: "Notes polished!", description: "Rewritten as a professional record." });
      }
    } catch (err) {
      const body = getApiErrorBody(err);
      // The /ai/* routes still signal these via boolean flags
      // (`subscriptionExpired` / `upgradeRequired`); accept the
      // forward-compat `errorCode` form too in case the server moves to
      // it later.
      const expired =
        body?.subscriptionExpired === true ||
        body?.errorCode === "subscription_expired";
      const upgrade =
        body?.upgradeRequired === true ||
        body?.errorCode === "upgrade_required";
      if (expired) {
        toast({
          title: "Subscribe to use AI polish",
          description:
            "Your trial has ended. Subscribe to keep using AI polish — your existing notes are safe.",
          variant: "destructive",
        });
      } else if (upgrade) {
        toast({
          title: "AI polish unavailable",
          description: body?.error ?? "Subscribe to keep using AI polish.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "AI polish failed",
          description: getApiErrorMessage(err, "Please try again in a moment."),
          variant: "destructive",
        });
      }
    }
  };

  const handlePolish = () => {
    runPolish();
  };

  const handleSave = async () => {
    const completedPhotos = photos.filter((p) => p.objectPath && !p.uploading);
    if (!shorthandNotes.trim() && !description.trim() && selectedAdjustments.length === 0 && completedPhotos.length === 0) {
      toast({ title: "Add some notes before saving", variant: "destructive" });
      return;
    }
    if (photoUploading) {
      toast({ title: "Please wait for photos to finish uploading", variant: "destructive" });
      return;
    }
    const photoUrls = completedPhotos.map((p) => p.objectPath);
    const fields: Record<string, unknown> = {};
    if (type === "nccd" && selectedAdjustments.length > 0) fields.adjustments = selectedAdjustments;
    if (photoUrls.length > 0) fields.photoUrls = photoUrls;

    try {
      await createRecord.mutateAsync({
        data: {
          type,
          date: format(date, "yyyy-MM-dd"),
          period: slot.label,
          courseId: slot.courseId ?? undefined,
          classId:  slot.classId  ?? undefined,
          subject:  slot.courseName ?? undefined,
          studentIds: selectedStudentIds as any,
          shorthandNotes: shorthandNotes || undefined,
          description:    description    || undefined,
          isConfidential: false,
          linkedToActivity: false,
          status: "active",
          fields: Object.keys(fields).length > 0 ? fields as any : undefined,
        } as any,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/records"] });
      toast({ title: "Record saved" });
      onSaved();
    } catch (err) {
      toast({
        title: "Couldn't save the record",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const isPolishing = polishText.isPending || nccdPolish.isPending;

  const handleSpeechResult = useCallback((transcript: string) => {
    setShorthandNotes((prev) => prev ? `${prev} ${transcript}` : transcript);
  }, []);

  const speech = useSpeechInput({ onResult: handleSpeechResult });

  return (
    <div className="border-t border-gray-200 bg-gray-50 divide-y divide-gray-100">

      {/* Type selector */}
      <div className="px-4 py-3">
        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Record type</div>
        <div className="flex flex-wrap gap-1.5">
          {(Object.entries(RECORD_TYPE_META) as [RecordType, typeof RECORD_TYPE_META[RecordType]][]).map(([t, m]) => (
            <button
              key={t}
              type="button"
              onClick={() => { setType(t); setAiDetectedIds([]); setSelectedAdjustments([]); }}
              className={`text-xs rounded-full px-3 py-1.5 font-semibold border transition-all ${
                type === t ? m.active : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Students */}
      <div className="px-4 py-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Students involved</div>
            {selectedStudentIds.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedStudentIds([])}
                className="text-[11px] text-gray-400 hover:text-gray-700 hover:underline"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Whole class quick-select */}
          {classStudentsData && classStudentsData.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedStudentIds(classStudentsData.map((s: any) => s.id))}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border font-medium transition-colors w-full ${
                selectedStudentIds.length === classStudentsData.length &&
                classStudentsData.every((s: any) => selectedStudentIds.includes(s.id))
                  ? "bg-primary/10 border-primary/30 text-primary"
                  : "bg-white border-gray-200 text-gray-600 hover:border-primary/40 hover:text-primary hover:bg-primary/5"
              }`}
            >
              <Users className="h-3.5 w-3.5 flex-shrink-0" />
              Whole class
              <span className="ml-auto text-gray-400 font-normal">{classStudentsData.length} students</span>
            </button>
          )}

          {/* Selected student badges */}
          {selectedStudentIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedStudentIds.map((id) => {
                const s = classStudents.find((x) => x.id === id);
                return (
                  <Badge
                    key={id}
                    variant="secondary"
                    className="cursor-pointer gap-1 text-xs font-medium pr-1.5"
                    onClick={() => toggleStudent(id)}
                  >
                    {s ? formatStudentName(s as DisplayStudent, accountDisplayMode) : "Hidden student"}
                    <X className="h-2.5 w-2.5 opacity-50 hover:opacity-100 ml-0.5" />
                  </Badge>
                );
              })}
            </div>
          )}

          {/* Dropdown trigger */}
          <Popover open={studentDropdownOpen} onOpenChange={(o) => { setStudentDropdownOpen(o); if (!o) setStudentSearch(""); }}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-left hover:border-gray-400 transition-colors"
              >
                <span className="flex items-center gap-2 text-gray-500">
                  <Users className="h-3.5 w-3.5 text-gray-400" />
                  {selectedStudentIds.length === 0
                    ? "Select students…"
                    : `${selectedStudentIds.length} student${selectedStudentIds.length !== 1 ? "s" : ""} selected`}
                </span>
                <ChevronsUpDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-72" align="start">
              {/* Search */}
              <div className="flex items-center border-b px-3 py-2 gap-2">
                <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <input
                  autoFocus
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  placeholder="Search students…"
                  className="flex-1 text-sm outline-none placeholder:text-gray-400 bg-transparent"
                />
                {studentSearch && (
                  <button type="button" onClick={() => setStudentSearch("")} className="text-gray-400 hover:text-gray-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Select all / Clear row */}
              <div className="flex items-center justify-between px-3 py-1.5 border-b bg-gray-50">
                <button
                  type="button"
                  onClick={() => setSelectedStudentIds(classStudents.map((s) => s.id))}
                  className="text-[11px] text-primary hover:underline font-medium"
                >
                  All students ({classStudents.length})
                </button>
                {selectedStudentIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedStudentIds([])}
                    className="text-[11px] text-gray-400 hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Student list */}
              <div className="max-h-52 overflow-y-auto divide-y">
                {filteredStudents.length === 0 ? (
                  <p className="px-4 py-5 text-sm text-center text-gray-400">
                    {studentSearch ? `No students match "${studentSearch}"` : "No students available"}
                  </p>
                ) : (
                  filteredStudents.map((s) => {
                    const selected = selectedStudentIds.includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => toggleStudent(s.id)}
                          className="h-3.5 w-3.5"
                        />
                        <span className="text-sm text-gray-800 flex-1">
                          {formatStudentName(s as DisplayStudent, accountDisplayMode)}
                          {s.yearLevel && <span className="text-gray-400 ml-2 text-xs">Yr {s.yearLevel}</span>}
                        </span>
                        {selected && <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />}
                      </label>
                    );
                  })
                )}
              </div>
            </PopoverContent>
          </Popover>
      </div>

      {/* Notes */}
      <div className="px-4 py-3 space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Quick notes</div>
            {speech.supported && (
              speech.listening ? (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium text-red-500 animate-pulse">Listening…</span>
                  <button
                    type="button"
                    onClick={speech.toggle}
                    aria-label="Stop dictation"
                    title="Stop dictation"
                    className="h-9 w-9 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors"
                  >
                    <Square className="h-4 w-4 fill-current" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={speech.toggle}
                  aria-label="Start dictation"
                  title="Start dictation"
                  className="h-9 w-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <Mic className="h-4 w-4" />
                </button>
              )
            )}
          </div>
          <AutoResizeTextarea
            value={shorthandNotes}
            onChange={(e) => setShorthandNotes(e.target.value)}
            placeholder={
              type === "nccd"
                ? "e.g. gave Tommy scaffolding sheet, brain break at 10 mins, read instructions aloud…"
                : "Jot rough notes — AI can turn them into a formal record."
            }
            className="text-sm bg-white"
          />
          {!speech.supported && (
            <p className="text-[11px] text-gray-400 mt-1">
              Dictation is not available in this browser. You can still use your phone keyboard microphone.
            </p>
          )}
          <DataMinimisationTip className="mt-1.5" />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 gap-2 text-xs h-8"
            onClick={handlePolish}
            disabled={isPolishing || !shorthandNotes.trim()}
          >
            {isPolishing
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Sparkles className="h-3.5 w-3.5 text-purple-500" />
            }
            {type === "nccd" ? "Polish & detect adjustments" : "Polish with AI"}
          </Button>
        </div>

        <div>
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            Formal description
            {description && <span className="ml-2 text-green-600 normal-case font-normal">✓ Ready</span>}
          </div>
          <AutoResizeTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              type === "nccd"
                ? "Professional NCCD adjustment record suitable for compliance documentation…"
                : "Professional record suitable for official documentation…"
            }
            className="text-sm bg-white"
          />
          {polishRedaction && (
            <div className="mt-2">
              <AiRedactionDisclosure
                summary={polishRedaction}
                surfaceKey="polish"
              />
            </div>
          )}
          <AIFeedback aiUsageId={polishAiUsageId} />
          <DataMinimisationTip className="mt-1.5" />
        </div>
      </div>

      {/* NCCD Adjustments */}
      {type === "nccd" && (
        <div className="px-4 py-3">
          <AdjustmentPanel
            adjustments={effectiveAdjustments}
            selectedIds={selectedAdjustments}
            onToggle={toggleAdjustment}
            personalIds={personalIds}
            aiDetectedIds={aiDetectedIds}
          />
        </div>
      )}

      {/* Photos */}
      <div className="px-4 py-3">
        {/* Hidden file inputs */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={(e) => handlePhotoFiles(e.target.files)}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handlePhotoFiles(e.target.files)}
        />

        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            Photos {photos.length > 0 && <span className="ml-1 normal-case font-normal text-gray-400">({photos.length})</span>}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 bg-white rounded-md px-2 py-1 hover:border-gray-400 transition-colors"
              title="Take a photo"
            >
              <Camera className="h-3.5 w-3.5" />
              Camera
            </button>
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 bg-white rounded-md px-2 py-1 hover:border-gray-400 transition-colors"
              title="Upload from gallery"
            >
              <ImagePlus className="h-3.5 w-3.5" />
              Upload
            </button>
          </div>
        </div>

        {photos.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {photos.map((photo) => (
              <div key={photo.previewUrl} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
                <img
                  src={photo.previewUrl}
                  alt="Photo attachment"
                  className={`w-full h-full object-cover ${photo.uploading ? "opacity-50" : ""}`}
                />
                {photo.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
                  </div>
                )}
                {!photo.uploading && (
                  <button
                    type="button"
                    onClick={() => removePhoto(photo.previewUrl)}
                    className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove photo"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            {/* Add more button */}
            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            >
              <ImagePlus className="h-5 w-5 mb-0.5" />
              <span className="text-[10px]">Add</span>
            </button>
          </div>
        ) : (
          <p className="text-xs text-gray-400">Attach photos from your camera or gallery.</p>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-3 flex items-center justify-between bg-white">
        <p className="text-[11px] text-gray-400">{format(date, "EEE d MMM")} · {slot.label}</p>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-8 text-xs"
            onClick={handleSave}
            disabled={createRecord.isPending}
           data-write-action>
            {createRecord.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Save record
          </Button>
        </div>
      </div>
    </div>
  );
}
